<script setup>
import { computed } from 'vue'
import { userTimelineDataType } from '~/constants/userTimelineConstants.js'

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const isLeft = computed(() => props.item.position === 0)
const isTop = computed(() => props.item.position === 1)
const isRight = computed(() => props.item.position === 2)
const isBottom = computed(() => props.item.position === 3)
const isBehind = computed(() => props.item.position === 4)
const isSideBySide = computed(() => isLeft.value || isRight.value)

const isImage = computed(() => props.item.dataType === userTimelineDataType.Image)
const isVideo = computed(() => props.item.dataType === userTimelineDataType.Video)

const aspectRatio = computed(() => {
  const { width, height } = props.item
  return width > 0 && height > 0 ? width / height : 16 / 9
})
</script>

<template>
  <div class="media-item" :class="{
    'media-left': isLeft,
    'media-right': isRight,
    'media-top': isTop,
    'media-bottom': isBottom,
    'media-behind': isBehind
  }">
    <!-- Behind layout -->
    <template v-if="isBehind">
      <div class="media-behind-wrapper" :style="{ aspectRatio: aspectRatio }">
        <template v-if="isVideo">
          <video
              class="media-image"
              :src="item.url"
              controls
              muted
              playsinline
          />
        </template>
        <template v-else>
          <v-img
              :src="item.url"
              class="media-image"
              cover
          />
        </template>
        <div class="media-overlay" v-if="item.title || item.subTitle">
          <div class="media-text">
            <div class="title">{{ item.title }}</div>
            <div class="subtitle">{{ item.subTitle }}</div>
          </div>
        </div>
      </div>
    </template>

    <!-- Other layouts -->
    <template v-else>
      <div class="media-content">
        <!-- Text for Left/Right — only when there's actual text (an empty
             .media-text otherwise leaves a big padded gap beside the image). -->
        <template v-if="isSideBySide && (item.title || item.subTitle)">
          <div class="media-text half">
            <div v-if="item.title" class="title">{{ item.title }}</div>
            <div v-if="item.subTitle" class="subtitle">{{ item.subTitle }}</div>
          </div>
        </template>

        <div class="media-media" :class="{ half: isSideBySide }">
          <template v-if="isVideo">
            <video
                class="media-image"
                :src="item.url"
                controls
                muted
                playsinline
            />
          </template>
          <template v-else>
            <v-img
                :src="item.url"
                cover
                class="media-image"
                :aspect-ratio="aspectRatio"
            />
          </template>
        </div>

        <!-- Text for Top/Bottom — only when present (empty text below an image
             rendered as a big margin under announcement images). -->
        <template v-if="(isTop || isBottom) && (item.title || item.subTitle)">
          <div class="media-text">
            <div v-if="item.title" class="title">{{ item.title }}</div>
            <div v-if="item.subTitle" class="subtitle">{{ item.subTitle }}</div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.media-item {
  .media-content {
    display: flex;
    gap: 16px;
    align-items: stretch; // 🧠 ensures side-by-side items align properly
    width: 100%;
  }

  &.media-left .media-content {
    flex-direction: row;
  }

  &.media-right .media-content {
    flex-direction: row-reverse;
  }

  &.media-top .media-content {
    flex-direction: column;
  }

  &.media-bottom .media-content {
    flex-direction: column-reverse;
  }

  .media-text {
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .title {
      font-weight: 700;
      font-size: 15px;
      letter-spacing: -0.2px;
    }

    .subtitle {
      font-size: 12.5px;
      color: rgb(var(--v-theme-charcoal));
      line-height: 1.6;
      margin-top: 4px;
    }

    &.half {
      width: 50%;
    }
  }

  .media-media {
    width: 100%;

    &.half {
      width: 50%;
    }

    .media-image {
      width: 100%;
      height: 100%;
      border-radius: 10px;
      object-fit: cover;
      display: block;
    }

    video.media-image {
      background: #000;
    }
  }

  &.media-behind .media-behind-wrapper {
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: 10px;

    .media-image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .media-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: flex-end;
      padding: 16px;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);

      .media-text {
        color: white;

        .title {
          font-size: 1.5rem;
          font-weight: bold;
        }

        .subtitle {
          font-size: 1rem;
          color: white;
        }
      }
    }
  }
}
</style>
